import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "cookie_choice";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [flipped, setFlipped] = useState(null); // "accept" | "necessary" | "close" | null

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved) return;
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  const commit = (value) => {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (e) { /* noop */ }
    setTimeout(() => {
      setExiting(true);
      setTimeout(() => setVisible(false), 470);
    }, 180);
  };

  const handleFlip = (which, value) => {
    if (flipped) return;
    setFlipped(which);
    setTimeout(() => commit(value), 600);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Preferenze cookie"
      className={`fixed z-[70] bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 md:max-w-md ${exiting ? "is-exiting-right" : "cookie-banner-enter"}`}
    >
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-neutral-950/80 backdrop-blur-xl p-6 md:p-7 text-white shadow-2xl">
        {/* Decorative glow */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-60"
             style={{ background: "radial-gradient(closest-side, rgba(229,0,125,0.75), rgba(229,0,125,0) 70%)" }} />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 w-56 h-56 rounded-full blur-3xl opacity-40"
             style={{ background: "radial-gradient(closest-side, rgba(255,58,160,0.6), rgba(255,58,160,0) 70%)" }} />

        {/* Close X */}
        <button
          data-testid="cookie-banner-close"
          aria-label="Chiudi"
          onClick={() => handleFlip("close", "necessary")}
          disabled={!!flipped}
          className="absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 rounded-2xl inline-flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #E5007D 0%, #FF3AA0 100%)" }}>
            <Cookie className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold uppercase tracking-widest text-sm">Cookie & privacy</div>
            <p className="mt-2 text-[13.5px] leading-relaxed text-white/80">
              Usiamo cookie tecnici per il funzionamento del sito. Nessun tracker invasivo.{" "}
              <Link
                to="/cookie"
                data-testid="cookie-banner-link"
                className="text-[color:var(--fucsia-2)] font-semibold hover:underline"
              >
                Scopri di più
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col md:flex-row gap-3">
          {/* Solo necessari */}
          <div className={`cookie-flip flex-1 ${flipped === "necessary" ? "is-flipped" : ""}`}>
            <div className="cookie-flip-inner relative">
              <button
                data-testid="cookie-banner-necessary"
                onClick={() => handleFlip("necessary", "necessary")}
                disabled={!!flipped}
                className="cookie-flip-face w-full h-11 rounded-full border border-white/10 hover:bg-white/5 text-white text-[11.5px] font-bold uppercase tracking-widest disabled:opacity-70"
              >
                Solo necessari
              </button>
              <div className="cookie-flip-back">
                <span className="text-[12px] uppercase tracking-widest">✓ Confermato</span>
              </div>
            </div>
          </div>

          {/* Accetta tutto */}
          <div className={`cookie-flip flex-1 ${flipped === "accept" ? "is-flipped" : ""}`}>
            <div className="cookie-flip-inner relative">
              <button
                data-testid="cookie-banner-accept"
                onClick={() => handleFlip("accept", "accepted")}
                disabled={!!flipped}
                className="cookie-flip-face w-full h-11 rounded-full bg-white text-black text-[11.5px] font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-70"
              >
                <Sparkles className="w-3.5 h-3.5" /> Accetta tutto
              </button>
              <div className="cookie-flip-back">
                <span className="text-[12px] uppercase tracking-widest">✓ Confermato</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
